/**
 * First touch attribution, captured on arrival.
 *
 * Two problems this solves, in order of how much they cost.
 *
 * 1. **Traffic from an AI assistant carries no utm.** Someone who asks ChatGPT
 *    or Perplexity for a dentist and follows the link arrives with a clean URL
 *    and a referrer of `chatgpt.com` or `perplexity.ai`. Reading only the query
 *    string, which is what the site did before, makes that visit
 *    indistinguishable from someone typing the address. The referrer is the
 *    only signal there is, so it has to be read, and it has to be read on
 *    arrival because it is gone after the first internal navigation.
 *
 * 2. **Ad params were being read too late.** They were resolved at click time
 *    from `window.location.search`. A visitor landing on the home page with a
 *    `gclid`, reading for a minute, opening a treatment page and only then
 *    tapping WhatsApp had already lost it: the second URL has no params. First
 *    touch is written once, on arrival, and every later click reads the store.
 *
 * Nothing here identifies a person. It records where the visit came from, not
 * who it is.
 */

export const ATTRIBUTION_STORAGE_KEY = "dr_filipe_attribution_v1"

export const TRACKING_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
  "gclid",
  "fbclid",
  "msclkid",
  "ttclid",
] as const

export type TrackingParamKey = (typeof TRACKING_PARAM_KEYS)[number]

export interface Attribution extends Partial<Record<TrackingParamKey, string>> {
  /** `ia`, `busca`, `social`, `referencia` or `direto`. */
  channel?: string
  /** Referring hostname, kept raw so a new assistant is still legible later. */
  referrer_host?: string
  /** Landing path of the first page seen, so we know which page pulled it in. */
  landing_path?: string
}

/**
 * Hostname fragments, matched against the referring host. Kept as a list of
 * substrings rather than exact hosts because these services move between
 * domains and regional subdomains more often than they change identity.
 */
const AI_HOSTS = [
  "chatgpt.com",
  "chat.openai.com",
  "openai.com",
  "perplexity.ai",
  "gemini.google.com",
  "bard.google.com",
  "claude.ai",
  "copilot.microsoft.com",
  "you.com",
  "phind.com",
  "poe.com",
]

const SEARCH_HOSTS = ["google.", "bing.com", "duckduckgo.com", "search.yahoo", "ecosia.org"]

const SOCIAL_HOSTS = [
  "instagram.com",
  "facebook.com",
  "fb.com",
  "l.facebook",
  "linkedin.com",
  "youtube.com",
  "t.co",
  "twitter.com",
  "x.com",
  "tiktok.com",
  "whatsapp.com",
]

export function classifyReferrer(host: string): string {
  const h = host.toLowerCase()

  if (AI_HOSTS.some((candidate) => h.includes(candidate))) {
    return "ia"
  }
  if (SEARCH_HOSTS.some((candidate) => h.includes(candidate))) {
    return "busca"
  }
  if (SOCIAL_HOSTS.some((candidate) => h.includes(candidate))) {
    return "social"
  }

  return "referencia"
}

export function readStoredAttribution(): Attribution {
  if (typeof window === "undefined") {
    return {}
  }

  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Attribution) : {}
  } catch {
    return {}
  }
}

/**
 * Writes first touch once and then leaves it alone. A visitor who arrives from
 * Perplexity, leaves, and comes back directly a week later still counts as
 * Perplexity, which is the honest reading: that is what caused the contact.
 */
export function captureAttribution() {
  if (typeof window === "undefined") {
    return {}
  }

  const stored = readStoredAttribution()
  const next: Attribution = { ...stored }
  let changed = false

  const search = new URLSearchParams(window.location.search)
  TRACKING_PARAM_KEYS.forEach((key) => {
    const value = search.get(key)?.trim()
    // Params on the current URL always win: a fresh ad click is a new campaign,
    // even for someone who has been here before.
    if (value && next[key] !== value) {
      next[key] = value
      changed = true
    }
  })

  if (!stored.channel) {
    const referrer = document.referrer

    if (!referrer) {
      next.channel = "direto"
      changed = true
    } else {
      try {
        const host = new URL(referrer).hostname
        // An internal link is not a referral.
        if (!host.endsWith(window.location.hostname)) {
          next.channel = classifyReferrer(host)
          next.referrer_host = host
          changed = true
        }
      } catch {
        // Malformed referrer, treat as unknown rather than guessing.
      }
    }
  }

  if (!stored.landing_path) {
    next.landing_path = window.location.pathname
    changed = true
  }

  if (changed) {
    try {
      window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(next))
    } catch {
      // Private mode or quota. Attribution is best effort and never blocks.
    }
  }

  return next
}
